import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PexIcon } from '../../../components/icons/PexIcon'
import { Button } from "../../../components/ui/button";
import type { SellerStoresStrings } from "../../../i18n/seller/stores";
import { API_BASE } from "../../../lib/api";
import { formatRelative } from "../../../lib/datetime";
import { useToastStore } from "../../../lib/toast";

type ExtensionStatus = {
  connected: boolean;
  key_prefix: string | null;
  created_at: string | null;
  last_seen_at: string | null;
  last_seen_version: string | null;
};

// The extension checks in every five minutes while an Etsy Orders tab is open.
// A gap of an hour means the tab (or the browser) is closed.
const QUIET_AFTER_MS = 60 * 60 * 1000;

function checkedInRecently(lastSeen: Date): boolean {
  return Date.now() - lastSeen.getTime() <= QUIET_AFTER_MS;
}

// Packaged from extensions/etsy-helper by frontend/Dockerfile at build time.
const DOWNLOAD_URL = `${import.meta.env.BASE_URL}downloads/pexify-etsy-helper.zip`;

const extensionQueryKey = (storeId: string) => ["store-integrations", storeId, "extension"];

async function readError(response: Response, fallback: string): Promise<Error> {
  const body = (await response.json().catch(() => null)) as {
    detail?: { message?: string };
  } | null;
  return new Error(body?.detail?.message ?? fallback);
}

export function EtsyExtensionPanel({
  storeId,
  copy,
}: {
  storeId: string;
  copy: SellerStoresStrings["extension"];
}) {
  const push = useToastStore((state) => state.push);
  const queryClient = useQueryClient();
  const [code, setCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  const status = useQuery({
    queryKey: extensionQueryKey(storeId),
    queryFn: async (): Promise<ExtensionStatus> => {
      const response = await fetch(`${API_BASE}/store-integrations/${storeId}/extension`);
      if (!response.ok) throw await readError(response, `extension ${response.status}`);
      return response.json() as Promise<ExtensionStatus>;
    },
  });

  const onError = (error: Error) =>
    push({ variant: "error", title: copy.actionFailed, description: error.message, duration_ms: 7000 });

  const create = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE}/store-integrations/${storeId}/extension/key`, {
        method: "POST",
      });
      if (!response.ok) throw await readError(response, `extension key ${response.status}`);
      return response.json() as Promise<{ key: string; status: ExtensionStatus }>;
    },
    onSuccess: (issued) => {
      setCode(issued.key);
      setCopied(false);
      setConfirmOff(false);
      queryClient.setQueryData(extensionQueryKey(storeId), issued.status);
      push({ variant: "success", title: copy.created, duration_ms: 4000 });
    },
    onError,
  });

  const turnOff = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE}/store-integrations/${storeId}/extension/key`, {
        method: "DELETE",
      });
      if (!response.ok) throw await readError(response, `extension key ${response.status}`);
      return response.json() as Promise<ExtensionStatus>;
    },
    onSuccess: (next) => {
      setCode(null);
      setConfirmOff(false);
      queryClient.setQueryData(extensionQueryKey(storeId), next);
      push({ variant: "success", title: copy.turnedOff, duration_ms: 4000 });
    },
    onError,
  });

  async function copyCode() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      push({ variant: "error", title: copy.copyFailed, duration_ms: 5000 });
    }
  }

  const current = status.data;
  const lastSeen = current?.last_seen_at ? new Date(current.last_seen_at) : null;
  const quiet = lastSeen !== null && !checkedInRecently(lastSeen);
  const tone = !current?.connected
    ? "muted"
    : lastSeen === null
      ? "muted"
      : quiet
        ? "warning"
        : "success";
  const statusText = !current?.connected
    ? copy.off
    : lastSeen === null
      ? copy.waiting
      : quiet
        ? copy.quiet(formatRelative(lastSeen))
        : copy.working(formatRelative(lastSeen));
  const busy = create.isPending || turnOff.isPending;

  return (
    <section aria-label={copy.title} className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-xl">
          <p className="text-sm font-semibold text-[var(--color-text)]">{copy.title}</p>
          <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{copy.description}</p>
          {status.isSuccess ? (
            <p className="mt-2 inline-flex items-center gap-2 text-xs font-medium text-[var(--color-text)]">
              <span
                aria-hidden="true"
                className={`h-2 w-2 shrink-0 rounded-full ${
                  tone === "success"
                    ? "bg-[var(--color-success)]"
                    : tone === "warning"
                      ? "bg-[var(--color-warning)]"
                      : "bg-[var(--color-text-muted)] opacity-50"
                }`}
              />
              {statusText}
            </p>
          ) : null}
        </div>
        {status.isSuccess ? (
          <div className="flex flex-wrap items-center gap-2">
            {confirmOff ? (
              <>
                <span className="text-xs text-[var(--color-text-muted)]">{copy.turnOffConfirm}</span>
                <Button variant="destructive" size="sm" disabled={busy} onClick={() => turnOff.mutate()}>
                  {copy.turnOffAction}
                </Button>
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmOff(false)}>
                  {copy.cancel}
                </Button>
              </>
            ) : (
              <>
                {current?.connected ? (
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmOff(true)}>
                    {copy.turnOff}
                  </Button>
                ) : null}
                <Button
                  variant={current?.connected ? "outline" : "primary"}
                  size="sm"
                  disabled={busy}
                  onClick={() => create.mutate()}
                >
                  {current?.connected ? copy.replace : copy.create}
                </Button>
              </>
            )}
          </div>
        ) : null}
      </div>

      <details
        className="text-xs text-[var(--color-text-muted)]"
        open={status.isSuccess && !current?.connected}
      >
        <summary className="cursor-pointer font-medium text-[var(--color-text)]">
          {copy.install.title}
        </summary>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>
            <a
              href={DOWNLOAD_URL}
              download
              className="font-medium text-[var(--color-text)] underline underline-offset-2"
            >
              {copy.install.download}
            </a>{" "}
            {copy.install.unzip}
          </li>
          <li>{copy.install.developerMode}</li>
          <li>{copy.install.loadUnpacked}</li>
          <li>{copy.install.pasteCode}</li>
          <li>{copy.install.keepOpen}</li>
        </ol>
      </details>

      {code ? (
        <div className="rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
          <p className="text-xs font-semibold text-[var(--color-text)]">{copy.codeTitle}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 select-all break-all rounded-[var(--radius-control)] bg-[var(--color-surface-muted)] px-3 py-2 font-mono text-xs text-[var(--color-text)]">
              {code}
            </code>
            <Button variant="outline" size="sm" onClick={() => void copyCode()}>
              {copied ? <PexIcon name="check" className="mr-1 h-3.5 w-3.5" size="xs" tone="inherit" /> : <PexIcon name="copy" className="mr-1 h-3.5 w-3.5" size="xs" tone="inherit" />}
              {copied ? copy.copied : copy.copy}
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-[var(--color-text-muted)]">{copy.codeHint}</p>
            <Button variant="ghost" size="sm" onClick={() => setCode(null)}>
              {copy.done}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
